export type DiffOp = "equal" | "insert" | "delete";

export interface DiffToken {
  op: DiffOp;
  value: string;
}

/** Split text into words + whitespace tokens so diffs stay readable. */
function tokenize(text: string): string[] {
  return text.match(/\s+|[^\s]+/g) ?? [];
}

/**
 * Word-level diff based on the classic LCS dynamic programming table.
 * Inputs are capped to keep the O(n*m) table reasonable for post-sized texts.
 */
export function diffWords(before: string, after: string): DiffToken[] {
  const a = tokenize(before).slice(0, 4000);
  const b = tokenize(after).slice(0, 4000);
  const n = a.length;
  const m = b.length;

  // lcs[i][j] = length of the longest common subsequence of a[i:] and b[j:]
  const lcs: Uint32Array[] = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }

  const raw: DiffToken[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      raw.push({ op: "equal", value: a[i] });
      i++;
      j++;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      raw.push({ op: "delete", value: a[i] });
      i++;
    } else {
      raw.push({ op: "insert", value: b[j] });
      j++;
    }
  }
  while (i < n) raw.push({ op: "delete", value: a[i++] });
  while (j < m) raw.push({ op: "insert", value: b[j++] });

  // Merge consecutive tokens sharing the same operation
  const merged: DiffToken[] = [];
  for (const token of raw) {
    const last = merged[merged.length - 1];
    if (last && last.op === token.op) last.value += token.value;
    else merged.push({ ...token });
  }
  return merged;
}

/** Accepts either pre-computed tokens, or a (before, after) pair of HTML/text. */
export function diffStats(input: DiffToken[] | string, after?: string) {
  const tokens: DiffToken[] = Array.isArray(input)
    ? input
    : diffWords(htmlToPlainText(input), htmlToPlainText(after ?? ""));
  const words = (s: string) => s.trim().split(/\s+/).filter(Boolean).length;
  let added = 0;
  let removed = 0;
  for (const t of tokens) {
    if (t.op === "insert") added += words(t.value);
    if (t.op === "delete") removed += words(t.value);
  }
  return { added, removed, unchanged: tokens.filter((t) => t.op === "equal").length };
}


// ── HTML-aware helpers used by the AI preview dialog ───────────────────────

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** Very small HTML → text conversion preserving block boundaries. */
export function htmlToPlainText(html: string): string {
  if (!html) return "";
  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|h[1-6]|blockquote|tr)>/gi, "\n")
    .replace(/<li\b[^>]*>/gi, "• ")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function tokensToHtml(tokens: DiffToken[], keep: DiffOp[]): string {
  return tokens
    .filter((t) => t.op === "equal" || keep.includes(t.op))
    .map((t) => {
      const value = escapeHtml(t.value).replace(/\n/g, "<br />");
      if (t.op === "insert") return `<ins class="diff-ins">${value}</ins>`;
      if (t.op === "delete") return `<del class="diff-del">${value}</del>`;
      return value;
    })
    .join("");
}

/** Single-column diff with insertions and deletions inlined. */
export function renderInlineDiff(beforeHtml: string, afterHtml: string): string {
  const tokens = diffWords(htmlToPlainText(beforeHtml), htmlToPlainText(afterHtml));
  return `<div class="whitespace-pre-wrap">${tokensToHtml(tokens, ["insert", "delete"])}</div>`;
}

/** Two-column diff: deletions on the left, insertions on the right. */
export function renderSideBySideDiff(
  beforeHtml: string,
  afterHtml: string,
): { beforeHtml: string; afterHtml: string } {
  const tokens = diffWords(htmlToPlainText(beforeHtml), htmlToPlainText(afterHtml));
  return {
    beforeHtml: `<div class="whitespace-pre-wrap">${tokensToHtml(tokens, ["delete"])}</div>`,
    afterHtml: `<div class="whitespace-pre-wrap">${tokensToHtml(tokens, ["insert"])}</div>`,
  };
}
